import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Upload, Send, CheckCircle, Loader2, ArrowLeft, FileText, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const applicationTypes = {
  tecnici: {
    title: 'Tecnici e Figure Manageriali',
    description: 'Candidati come Site Supervisor, Project Manager, HSE Manager, Commissioning Engineer o altre figure tecniche.',
    fields: ['role', 'experience_years', 'availability']
  },
  imprese: {
    title: 'Imprese Subappaltatrici',
    description: 'Registra la tua impresa nel nostro network di fornitori qualificati.',
    fields: ['company_name', 'specialization', 'certifications']
  },
  freelance: {
    title: 'Collaboratori Freelance',
    description: 'Diventa Business Developer o Recruiter freelance e collabora con noi.',
    fields: ['collaboration_type']
  }
};

const roleOptions = [
  'Site Supervisor / Capo Cantiere',
  'Project Manager',
  'Project Engineer',
  'HSE Manager',
  'HSE Supervisor',
  'Commissioning Manager',
  'Commissioning Engineer',
  'Quality Inspector',
  'Altro'
];

const specializationOptions = [
  'Carpenteria metallica e strutturale',
  'Montaggi meccanici',
  'Impianti elettrici e strumentali',
  'Saldature certificate',
  'Manutenzione impiantistica',
  'Altro'
];

const collaborationOptions = [
  'Business Developer',
  'Recruiter',
  'Entrambi'
];

export default function ApplicationPage() {
  const { type } = useParams();
  const appType = applicationTypes[type] || applicationTypes.tecnici;
  
  const [formData, setFormData] = useState({
    application_type: type || 'tecnici',
    name: '',
    email: '',
    phone: '',
    role: '',
    experience_years: '',
    availability: '',
    company_name: '',
    specialization: '',
    certifications: '',
    collaboration_type: '',
    cv_url: '',
    notes: ''
  });
  
  const [cvFile, setCvFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Formato file non supportato. Usa PDF, DOC o DOCX.');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File troppo grande. Massimo 10MB.');
      return;
    }

    setCvFile(file);
    setIsUploading(true);

    try {
      // Get signature from backend
      const sigResponse = await axios.get(`${API}/cloudinary/signature?resource_type=raw&folder=cv_uploads`);
      const sig = sigResponse.data;

      // Upload to Cloudinary with PUBLIC access mode
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('api_key', sig.api_key);
      formDataUpload.append('timestamp', sig.timestamp);
      formDataUpload.append('signature', sig.signature);
      formDataUpload.append('folder', sig.folder);
      formDataUpload.append('access_mode', sig.access_mode || 'public');  // Use signed access_mode

      console.log('Uploading CV with params:', { 
        folder: sig.folder, 
        access_mode: sig.access_mode,
        timestamp: sig.timestamp 
      });

      const uploadResponse = await fetch(
        `https://api.cloudinary.com/v1_1/${sig.cloud_name}/raw/upload`,
        { method: 'POST', body: formDataUpload }
      );

      const uploadResult = await uploadResponse.json();
      
      if (uploadResult.secure_url) {
        setFormData(prev => ({ ...prev, cv_url: uploadResult.secure_url }));
        toast.success('CV caricato con successo!');
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      toast.error('Errore nel caricamento del CV. Riprova.');
      console.error(error);
      setCvFile(null);
    } finally {
      setIsUploading(false);
    }
  };

  const removeFile = () => {
    setCvFile(null);
    setFormData(prev => ({ ...prev, cv_url: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Clean up data before sending - convert empty strings to null for optional fields
      const cleanedData = {
        ...formData,
        experience_years: formData.experience_years ? parseInt(formData.experience_years, 10) : null,
        role: formData.role || null,
        availability: formData.availability || null,
        company_name: formData.company_name || null,
        specialization: formData.specialization || null,
        certifications: formData.certifications || null,
        collaboration_type: formData.collaboration_type || null,
        cv_url: formData.cv_url || null,
        notes: formData.notes || null
      };
      
      await axios.post(`${API}/applications`, cleanedData);
      setIsSubmitted(true);
      toast.success('Candidatura inviata con successo!');
    } catch (error) {
      toast.error('Errore nell\'invio della candidatura. Riprova.');
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!applicationTypes[type]) {
    return (
      <div data-testid="application-not-found">
        <Navbar />
        <main className="min-h-screen bg-[#F5F5F7] py-20">
          <div className="max-w-2xl mx-auto px-4 text-center">
            <h1 className="text-3xl font-bold text-[#1A1A2E] mb-4">Pagina non trovata</h1>
            <Link to="/lavora-con-noi" className="btn-primary inline-block">Torna a Lavora con noi</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div data-testid={`application-page-${type}`}>
      <Navbar />
      <main>
        {/* Hero Section */}
        <section className="hero-dark py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link to="/lavora-con-noi" className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors">
              <ArrowLeft size={20} />
              Torna a Lavora con noi
            </Link>
            <div className="accent-line mb-6"></div>
            <h1 data-testid="application-headline" className="text-3xl md:text-5xl font-extrabold text-white mb-4">
              {appType.title}
            </h1>
            <p className="text-lg text-gray-300 max-w-2xl">
              {appType.description}
            </p>
          </div>
        </section>

        {/* Form Section */}
        <section className="py-16 md:py-24 bg-[#F5F5F7] noise-bg relative">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            {isSubmitted ? (
              <div data-testid="application-success" className="bg-white border-2 border-[#27AE60] p-12 text-center">
                <CheckCircle className="w-20 h-20 text-[#27AE60] mx-auto mb-6" />
                <h2 className="text-2xl font-bold text-[#1A1A2E] mb-4">Candidatura inviata!</h2>
                <p className="text-[#555555] mb-8">
                  Grazie per il tuo interesse. Esamineremo il tuo profilo e ti contatteremo entro 48 ore lavorative.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/" className="btn-primary">Torna alla Home</Link>
                  <button onClick={() => setIsSubmitted(false)} className="btn-secondary">Invia altra candidatura</button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="application-form" className="bg-white border-2 border-gray-200 p-8 md:p-12">
                <h2 className="text-2xl font-bold text-[#1A1A2E] mb-8">Compila il form</h2>
                
                {/* Common Fields */}
                <div className="space-y-6 mb-8">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className="font-bold text-[#1A1A2E] mb-2 block">Nome e Cognome *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        data-testid="app-name-input"
                        className="input-industrial w-full"
                        placeholder="Mario Rossi"
                      />
                    </div>
                    <div>
                      <label className="font-bold text-[#1A1A2E] mb-2 block">Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        data-testid="app-email-input"
                        className="input-industrial w-full"
                        placeholder="mario.rossi@email.com"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="font-bold text-[#1A1A2E] mb-2 block">Telefono *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      data-testid="app-phone-input"
                      className="input-industrial w-full"
                      placeholder="+39 333 1234567"
                    />
                  </div>
                </div>

                {/* Type-specific Fields */}
                {type === 'tecnici' && (
                  <div className="space-y-6 mb-8 pt-8 border-t border-gray-200">
                    <h3 className="font-bold text-[#1A1A2E]">Informazioni professionali</h3>
                    <div>
                      <label className="font-bold text-[#1A1A2E] mb-2 block">Ruolo *</label>
                      <select
                        name="role"
                        value={formData.role}
                        onChange={handleChange}
                        required
                        data-testid="app-role-select"
                        className="input-industrial w-full"
                      >
                        <option value="">Seleziona un ruolo</option>
                        {roleOptions.map(role => (
                          <option key={role} value={role}>{role}</option>
                        ))}
                      </select>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div>
                        <label className="font-bold text-[#1A1A2E] mb-2 block">Anni di esperienza *</label>
                        <input
                          type="number"
                          name="experience_years"
                          value={formData.experience_years}
                          onChange={handleChange}
                          required
                          min="0"
                          data-testid="app-experience-input"
                          className="input-industrial w-full"
                          placeholder="5"
                        />
                      </div>
                      <div>
                        <label className="font-bold text-[#1A1A2E] mb-2 block">Disponibilità</label>
                        <select
                          name="availability"
                          value={formData.availability}
                          onChange={handleChange}
                          data-testid="app-availability-select"
                          className="input-industrial w-full"
                        >
                          <option value="">Seleziona</option>
                          <option value="immediata">Immediata</option>
                          <option value="2_settimane">2 settimane</option>
                          <option value="1_mese">1 mese</option>
                          <option value="altro">Da definire</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {type === 'imprese' && (
                  <div className="space-y-6 mb-8 pt-8 border-t border-gray-200">
                    <h3 className="font-bold text-[#1A1A2E]">Informazioni aziendali</h3>
                    <div>
                      <label className="font-bold text-[#1A1A2E] mb-2 block">Nome Azienda *</label>
                      <input
                        type="text"
                        name="company_name"
                        value={formData.company_name}
                        onChange={handleChange}
                        required
                        data-testid="app-company-input"
                        className="input-industrial w-full"
                        placeholder="Azienda Srl"
                      />
                    </div>
                    <div>
                      <label className="font-bold text-[#1A1A2E] mb-2 block">Specializzazione *</label>
                      <select
                        name="specialization"
                        value={formData.specialization}
                        onChange={handleChange}
                        required
                        data-testid="app-specialization-select"
                        className="input-industrial w-full"
                      >
                        <option value="">Seleziona una specializzazione</option>
                        {specializationOptions.map(spec => (
                          <option key={spec} value={spec}>{spec}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="font-bold text-[#1A1A2E] mb-2 block">Certificazioni</label>
                      <input
                        type="text"
                        name="certifications"
                        value={formData.certifications}
                        onChange={handleChange}
                        data-testid="app-certifications-input"
                        className="input-industrial w-full"
                        placeholder="ISO 9001, SOA, etc."
                      />
                    </div>
                  </div>
                )}

                {type === 'freelance' && (
                  <div className="space-y-6 mb-8 pt-8 border-t border-gray-200">
                    <h3 className="font-bold text-[#1A1A2E]">Tipo di collaborazione</h3>
                    <div>
                      <label className="font-bold text-[#1A1A2E] mb-2 block">Collaborazione *</label>
                      <select
                        name="collaboration_type"
                        value={formData.collaboration_type}
                        onChange={handleChange}
                        required
                        data-testid="app-collaboration-select"
                        className="input-industrial w-full"
                      >
                        <option value="">Seleziona</option>
                        {collaborationOptions.map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {/* CV Upload */}
                <div className="space-y-4 mb-8 pt-8 border-t border-gray-200">
                  <h3 className="font-bold text-[#1A1A2E]">Carica il tuo CV</h3>
                  
                  {cvFile ? (
                    <div data-testid="cv-uploaded" className="flex items-center justify-between bg-[#F5F5F7] p-4 border-2 border-gray-200">
                      <div className="flex items-center gap-3">
                        <FileText className="text-[#27AE60]" size={24} />
                        <div>
                          <p className="font-bold text-[#1A1A2E]">{cvFile.name}</p>
                          <p className="text-sm text-[#888888]">{(cvFile.size / 1024 / 1024).toFixed(2)} MB</p>
                        </div>
                      </div>
                      <button type="button" onClick={removeFile} className="text-[#E94560] hover:text-red-700">
                        <X size={20} />
                      </button>
                    </div>
                  ) : (
                    <label 
                      data-testid="cv-upload-label"
                      className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-gray-300 hover:border-[#27AE60] cursor-pointer transition-colors bg-white"
                    >
                      {isUploading ? (
                        <div className="text-center">
                          <Loader2 className="w-10 h-10 text-[#27AE60] animate-spin mx-auto mb-2" />
                          <p className="text-[#555555]">Caricamento in corso...</p>
                        </div>
                      ) : (
                        <div className="text-center">
                          <Upload className="w-10 h-10 text-[#888888] mx-auto mb-2" />
                          <p className="text-[#555555]">Clicca o trascina il file qui</p>
                          <p className="text-sm text-[#888888]">PDF, DOC, DOCX (max 10MB)</p>
                        </div>
                      )}
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={handleFileChange}
                        className="hidden"
                        data-testid="cv-file-input"
                      />
                    </label>
                  )}
                </div>

                {/* Notes */}
                <div className="mb-8">
                  <label className="font-bold text-[#1A1A2E] mb-2 block">Note aggiuntive</label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    rows={4}
                    data-testid="app-notes-input"
                    className="input-industrial w-full resize-none"
                    placeholder="Altre informazioni utili..."
                  />
                </div>

                {/* Privacy & Submit */}
                <div className="flex items-start gap-3 mb-8">
                  <input 
                    type="checkbox" 
                    required 
                    id="privacy"
                    data-testid="app-privacy-checkbox"
                    className="mt-1"
                  />
                  <label htmlFor="privacy" className="text-sm text-[#555555]">
                    Accetto la <a href="#" className="text-[#E94560] underline">Privacy Policy</a> e il trattamento dei miei dati personali.
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting || isUploading}
                  data-testid="app-submit-button"
                  className="btn-primary w-full inline-flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <><Loader2 className="animate-spin" size={20} /> Invio in corso...</>
                  ) : (
                    <><Send size={20} /> Invia candidatura</>
                  )}
                </button>
              </form>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
